import {
  Badge,
  Box,
  Card,
  Flex,
  HStack,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { z } from "zod"

import { type UserPublic, UsersService } from "@/client"
import AddUser from "@/components/Admin/AddUser"
import { UserActionsMenu } from "@/components/Common/UserActionsMenu"
import { PageShell } from "@/components/Layout"
import PendingUsers from "@/components/Pending/PendingUsers"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination.tsx"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

const usersSearchSchema = z.object({
  page: z.number().catch(1),
})

const PER_PAGE = 5

function getUsersQueryOptions({ page }: { page: number }) {
  return {
    queryFn: () =>
      UsersService.readUsers({ skip: (page - 1) * PER_PAGE, limit: PER_PAGE }),
    queryKey: ["users", { page }],
  }
}

export const Route = createFileRoute("/_layout/admin")({
  component: Admin,
  validateSearch: (search) => usersSearchSchema.parse(search),
})

function UsersTable() {
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])
  const navigate = useNavigate({ from: Route.fullPath })
  const { page } = Route.useSearch()

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getUsersQueryOptions({ page }),
    placeholderData: (prevData) => prevData,
  })

  const setPage = (page: number) =>
    navigate({
      search: (prev: { [key: string]: string }) => ({ ...prev, page }),
    })

  const users = data?.data.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0

  if (isLoading) {
    return <PendingUsers />
  }

  return (
    <>
      {/* Desktop */}
      <Box display={{ base: "none", md: "block" }}>
        <Table.Root size={{ base: "sm", md: "md" }}>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader w="20%">Full name</Table.ColumnHeader>
              <Table.ColumnHeader w="25%">Email</Table.ColumnHeader>
              <Table.ColumnHeader w="15%">Role</Table.ColumnHeader>
              <Table.ColumnHeader w="20%">Status</Table.ColumnHeader>
              <Table.ColumnHeader w="20%">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {users?.map((user) => (
              <Table.Row key={user.id} opacity={isPlaceholderData ? 0.5 : 1}>
                <Table.Cell
                  w="20%"
                  color={!user.full_name ? "gray.400" : "inherit"}
                >
                  {user.full_name || "N/A"}
                  {currentUser?.id === user.id && (
                    <Badge ml="1" colorPalette="teal">
                      You
                    </Badge>
                  )}
                </Table.Cell>
                <Table.Cell w="25%">{user.email}</Table.Cell>
                <Table.Cell w="15%">
                  {user.is_superuser ? "Superuser" : "User"}
                </Table.Cell>
                <Table.Cell w="20%">
                  {user.is_active ? "Active" : "Inactive"}
                </Table.Cell>
                <Table.Cell w="20%">
                  <UserActionsMenu
                    user={user}
                    disabled={currentUser?.id === user.id}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Mobile cards */}
      <VStack display={{ base: "flex", md: "none" }} gap={3} align="stretch">
        {users?.map((user) => (
          <Card.Root key={user.id} opacity={isPlaceholderData ? 0.5 : 1}>
            <Card.Body>
              <Flex justify="space-between" align="center">
                <Text
                  fontWeight="medium"
                  color={!user.full_name ? "gray.400" : "inherit"}
                >
                  {user.full_name || "N/A"}
                </Text>
                {currentUser?.id === user.id && (
                  <Badge colorPalette="teal" size="sm">
                    You
                  </Badge>
                )}
              </Flex>
              <Text fontSize="sm" color="fg.muted" mt={1}>
                {user.email}
              </Text>
              <Flex justify="space-between" align="center" mt={3}>
                <HStack gap={2}>
                  <Badge colorPalette="blue" size="sm">
                    {user.is_superuser ? "Superuser" : "User"}
                  </Badge>
                  <Badge
                    colorPalette={user.is_active ? "green" : "gray"}
                    size="sm"
                  >
                    {user.is_active ? "Active" : "Inactive"}
                  </Badge>
                </HStack>
                <UserActionsMenu
                  user={user}
                  disabled={currentUser?.id === user.id}
                />
              </Flex>
            </Card.Body>
          </Card.Root>
        ))}
      </VStack>
      <Flex justifyContent="flex-end" mt={4}>
        <PaginationRoot
          count={count}
          pageSize={PER_PAGE}
          onPageChange={({ page }) => setPage(page)}
        >
          <Flex>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </Flex>
        </PaginationRoot>
      </Flex>
    </>
  )
}

function Admin() {
  const breadcrumbs = useRouteBreadcrumbs("admin.users")

  return (
    <PageShell title="Users Management" breadcrumbs={breadcrumbs}>
      <AddUser />
      <UsersTable />
    </PageShell>
  )
}
